import type { Guide, GuideScope } from "@/document/model/guides";
import { frameGuides, guideScopesEqual, pageGuides, requireGuide, type GuideSelection } from "@/document/model/guides";
import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import type { FrameNode } from "@/document/model/nodes";
import type { Command, CommandContext, CommandResult } from "./Command";

function emptyResult(): CommandResult {
  return { changedNodeIds: [], affectedBounds: [] };
}

export class AddGuideCommand implements Command {
  readonly id = "guide.add";
  readonly label = "Add Guide";

  constructor(
    private readonly scope: GuideScope,
    private readonly guide: Guide,
  ) {}

  execute(context: CommandContext): CommandResult {
    this.guidesForScope(context.document).push(structuredClone(this.guide));
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    const guides = this.guidesForScope(context.document);
    const index = guides.findIndex(guide => guide.id === this.guide.id);
    if (index >= 0) {
      guides.splice(index, 1);
    }
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private guidesForScope(document: CurvaDocument): Guide[] {
    if (this.scope.kind === "page") {
      const pageId = this.scope.pageId;
      const page = document.pages.find(candidate => candidate.id === pageId);
      if (!page) {
        throw new Error("Page not found.");
      }
      page.guides = pageGuides(page);
      return page.guides;
    }

    const frame = requireFrame(document, this.scope.frameId);
    frame.guides = frameGuides(frame);
    return frame.guides;
  }
}

export class MoveGuideCommand implements Command {
  readonly id = "guide.move";
  readonly label = "Move Guide";

  constructor(
    private readonly selection: { id: string; scope: GuideScope },
    private readonly fromPosition: number,
    private readonly toPosition: number,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyPosition(context, this.toPosition);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyPosition(context, this.fromPosition);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof MoveGuideCommand &&
      command.selection.id === this.selection.id &&
      guideScopesEqual(command.selection.scope, this.selection.scope);
  }

  merge(command: Command): Command {
    if (!(command instanceof MoveGuideCommand)) {
      throw new Error("Cannot merge guide move commands.");
    }

    return new MoveGuideCommand(this.selection, this.fromPosition, command.toPosition);
  }

  private applyPosition(context: CommandContext, position: number): CommandResult {
    const { guide } = requireGuide(context.document, this.selection);
    guide.position = position;
    return emptyResult();
  }
}

export class RelocateGuideCommand implements Command {
  readonly id = "guide.relocate";
  readonly label = "Move Guide";
  private removedGuide?: Guide;

  constructor(
    private readonly selection: GuideSelection,
    private readonly fromPosition: number,
    private readonly toScope: GuideScope,
    private readonly toPosition: number,
  ) {}

  execute(context: CommandContext): CommandResult {
    const { guide } = requireGuide(context.document, this.selection);
    this.removedGuide = structuredClone(guide);
    this.removeGuide(context.document);
    const nextGuide = { ...structuredClone(this.removedGuide), position: this.toPosition };
    this.guidesForScope(context.document, this.toScope).push(nextGuide);
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (!this.removedGuide) {
      throw new Error("Cannot undo guide relocate.");
    }

    this.removeGuideAtScope(context.document, this.toScope, this.removedGuide.id);
    const guide = { ...structuredClone(this.removedGuide), position: this.fromPosition };
    this.guidesForScope(context.document, this.selection.scope).push(guide);
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private removeGuide(document: CurvaDocument): void {
    this.removeGuideAtScope(document, this.selection.scope, this.selection.id);
  }

  private removeGuideAtScope(document: CurvaDocument, scope: GuideScope, guideId: string): void {
    const guides = this.guidesForScope(document, scope);
    const index = guides.findIndex(guide => guide.id === guideId);
    if (index >= 0) {
      guides.splice(index, 1);
    }
  }

  private guidesForScope(document: CurvaDocument, scope: GuideScope): Guide[] {
    if (scope.kind === "page") {
      const page = document.pages.find(candidate => candidate.id === scope.pageId);
      if (!page) {
        throw new Error("Page not found.");
      }
      page.guides = pageGuides(page);
      return page.guides;
    }

    const frame = requireFrame(document, scope.frameId);
    frame.guides = frameGuides(frame);
    return frame.guides;
  }
}

export class DeleteGuideCommand implements Command {
  readonly id = "guide.delete";
  readonly label = "Delete Guide";
  private removedGuide?: Guide;
  private removedIndex?: number;

  constructor(private readonly selection: { id: string; scope: GuideScope }) {}

  execute(context: CommandContext): CommandResult {
    const guides = this.guidesForScope(context.document);
    const index = guides.findIndex(guide => guide.id === this.selection.id);
    if (index < 0) {
      throw new Error("Guide not found.");
    }

    this.removedIndex = index;
    this.removedGuide = structuredClone(guides[index]!);
    guides.splice(index, 1);
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (!this.removedGuide || this.removedIndex === undefined) {
      throw new Error("Cannot undo guide delete.");
    }

    const guides = this.guidesForScope(context.document);
    guides.splice(this.removedIndex, 0, structuredClone(this.removedGuide));
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private guidesForScope(document: CurvaDocument): Guide[] {
    if (this.selection.scope.kind === "page") {
      const pageId = this.selection.scope.pageId;
      const page = document.pages.find(candidate => candidate.id === pageId);
      if (!page) {
        throw new Error("Page not found.");
      }
      page.guides = pageGuides(page);
      return page.guides;
    }

    const frame = requireFrame(document, this.selection.scope.frameId);
    frame.guides = frameGuides(frame);
    return frame.guides;
  }
}

function requireFrame(document: CurvaDocument, frameId: string): FrameNode {
  const frame = findSceneNode(document, frameId);
  if (!frame || frame.type !== "frame") {
    throw new Error("Frame not found.");
  }
  return frame;
}
